import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeftRight, Loader2 } from "lucide-react";
import type { Item } from "@shared/schema";

const BARTER_CATEGORIES = [
  { value: "produce", label: "Produce" },
  { value: "eggs", label: "Eggs & Dairy" },
  { value: "baked", label: "Baked Goods" },
  { value: "prepared", label: "Prepared Foods" },
  { value: "meat", label: "Meat & Poultry" },
  { value: "pantry", label: "Pantry" },
  { value: "flowers", label: "Flowers" },
  { value: "homemade", label: "Homemade / Crafted" },
  { value: "garden", label: "Garden" },
  { value: "other", label: "Other" },
] as const;

type BarterItem = Item & {
  barterEnabled?: boolean | null;
  barterAcceptCategories?: string[] | null;
  barterNote?: string | null;
};

function OwnerSettings({ item }: { item: BarterItem }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [enabled, setEnabled] = useState(!!item.barterEnabled);
  const [categories, setCategories] = useState<string[]>(item.barterAcceptCategories ?? []);
  const [note, setNote] = useState(item.barterNote ?? "");

  const save = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("PATCH", `/api/items/${item.id}/barter`, {
        barterEnabled: enabled,
        barterAcceptCategories: categories,
        barterNote: note.trim() || null,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/items/${item.id}`] });
      queryClient.invalidateQueries({ queryKey: ["/api/seller/items"] });
      toast({ title: "Trade settings saved" });
    },
    onError: (e: any) => toast({ title: "Couldn't save", description: e.message, variant: "destructive" }),
  });

  const toggle = (value: string) =>
    setCategories((prev) => (prev.includes(value) ? prev.filter((c) => c !== value) : [...prev, value]));

  return (
    <Card className="border-dashed">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ArrowLeftRight className="h-4 w-4" />
            <span className="font-medium">Accept trades for this listing</span>
          </div>
          <Switch checked={enabled} onCheckedChange={setEnabled} />
        </div>

        {enabled && (
          <>
            <div>
              <p className="text-xs text-muted-foreground mb-2">
                What will you take in trade? Leave empty to accept anything.
              </p>
              <div className="flex flex-wrap gap-1.5">
                {BARTER_CATEGORIES.map((c) => (
                  <Badge
                    key={c.value}
                    variant={categories.includes(c.value) ? "default" : "outline"}
                    className="cursor-pointer"
                    onClick={() => toggle(c.value)}
                  >
                    {c.label}
                  </Badge>
                ))}
              </div>
            </div>
            <Textarea
              placeholder="What are you looking for? (optional)"
              value={note}
              maxLength={500}
              onChange={(e) => setNote(e.target.value)}
              className="min-h-[60px]"
            />
          </>
        )}

        <Button size="sm" disabled={save.isPending} onClick={() => save.mutate()}>
          {save.isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
          Save trade settings
        </Button>
      </CardContent>
    </Card>
  );
}

function ProposeTrade({ item }: { item: BarterItem }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const [message, setMessage] = useState("");

  const { data: myItems = [], isLoading } = useQuery<BarterItem[]>({
    queryKey: ["/api/seller/items"],
    enabled: open,
  });

  const accept = item.barterAcceptCategories ?? [];
  const eligible = myItems.filter(
    (mi) => mi.id !== item.id && (accept.length === 0 || (mi.category != null && accept.includes(mi.category))),
  );

  const propose = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/barter/offers", {
        targetItemId: item.id,
        offeredItemId: selected,
        message: message.trim() || undefined,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/barter/offers"] });
      toast({ title: "Trade offer sent" });
      setOpen(false);
      setSelected(null);
      setMessage("");
    },
    onError: (e: any) => toast({ title: "Couldn't send offer", description: e.message, variant: "destructive" }),
  });

  if (!open) {
    return (
      <Button variant="outline" className="w-full" onClick={() => setOpen(true)}>
        <ArrowLeftRight className="h-4 w-4 mr-2" /> Propose a trade
      </Button>
    );
  }

  return (
    <Card className="border-dashed">
      <CardContent className="p-4 space-y-3">
        <p className="font-medium">Offer one of your listings</p>
        {item.barterNote && (
          <p className="text-xs text-muted-foreground">They're looking for: {item.barterNote}</p>
        )}
        {isLoading ? (
          <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : eligible.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {accept.length > 0
              ? "None of your listings match what this seller accepts."
              : "You have no listings to offer yet."}
          </p>
        ) : (
          <div className="space-y-1.5 max-h-56 overflow-y-auto">
            {eligible.map((mi) => (
              <button
                key={mi.id}
                type="button"
                onClick={() => setSelected(mi.id)}
                className={`w-full text-left px-3 py-2 rounded-md border text-sm ${
                  selected === mi.id ? "border-primary bg-primary/5" : "border-border"
                }`}
              >
                {mi.name}
              </button>
            ))}
          </div>
        )}
        <Textarea
          placeholder="Add a note (optional)"
          value={message}
          maxLength={1000}
          onChange={(e) => setMessage(e.target.value)}
          className="min-h-[60px]"
        />
        <div className="flex gap-2">
          <Button
            size="sm"
            className="flex-1"
            disabled={!selected || propose.isPending}
            onClick={() => propose.mutate()}
          >
            {propose.isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            Send offer
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
            Cancel
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export function BarterPanel({ item }: { item: BarterItem }) {
  const { user } = useAuth();
  if (!user?.canBarter) return null;

  const isOwner = item.sellerId === user.id;
  if (isOwner) return <OwnerSettings item={item} />;
  if (!item.barterEnabled) return null;
  return <ProposeTrade item={item} />;
}
